import { useEffect, useState } from 'react';
import { useUserStore } from '@/stores/userStore';
import { Climber } from '@/art';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { CoinIcon } from '@/components/CurrencyIcon';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';
import { adsAvailable, showRewarded } from '@/ads/adService';
import { PreviewDialog } from '@/shop/PreviewDialog';
import { CharacterAnimationPreview } from '@/characters/CharacterAnimationPreview';
import {
  characterApi,
  RARITY_COLOR,
  type CharacterItem,
} from '@/characters/characterApi';

/**
 * Character store section embedded in the Shop screen. Players BUY climbers
 * here; equipping happens in the Inventory (BAG). Owned items show an OWNED
 * badge instead of a buy action.
 */
export function CharactersShopSection() {
  const t = useT();
  const profile = useUserStore((s) => s.profile);
  const setProfile = useUserStore((s) => s.setProfile);

  const [items, setItems] = useState<CharacterItem[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);
  const [previewItem, setPreviewItem] = useState<CharacterItem | null>(null);

  const load = () =>
    characterApi.list()
      .then((r) => {
        setItems(r.items);
        setProfile({ coins: r.coins, gems: r.gems });
      })
      .catch((e) => flash('err', String(e)));

  useEffect(() => { void load(); }, []);

  function flash(kind: 'ok' | 'err', text: string) {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 2200);
  }

  async function buy(it: CharacterItem, currency: 'coins' | 'gems') {
    setBusyId(it.id);
    try {
      const r = await characterApi.buy(it.id, currency);
      audio.play('ui.shop_purchase');
      setProfile({ coins: r.coins, gems: r.gems });
      await load();
      flash('ok', t('chars.unlocked', { name: it.name }));
    } catch (e: any) {
      audio.play('ui.error');
      flash('err', e?.message ?? t('chars.purchase_failed'));
    }
    setBusyId(null);
  }

  async function startTrial(it: CharacterItem) {
    setBusyId(it.id);
    try {
      const earned = await showRewarded();
      if (!earned) { setBusyId(null); return; }
      const { trialApi } = await import('@/shop/trialApi');
      const r = await trialApi.start('character', it.id);
      audio.play('ui.shop_purchase');
      await load(); // refresh so trialUsesLeft is set → the TRY button hides
      flash('ok', t('shop.trial_started', { name: it.name, n: r.usesLeft }));
    } catch (e: any) {
      audio.play('ui.error');
      flash('err', e?.message ?? t('chars.purchase_failed'));
    }
    setBusyId(null);
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tag" style={{ padding: '8px 2px 8px', color: 'var(--c-purple)' }}>
        {t('chars.shop_section')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!items ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('chars.loading')}
          </div>
        ) : items.map((it) => (
          <ShopCharacterCard
            key={it.id}
            item={it}
            avatarColor={profile.avatarColor}
            busy={busyId === it.id}
            canCoins={profile.coins >= it.priceCoins}
            canGems={profile.gems >= it.priceGems}
            onBuy={(cur) => buy(it, cur)}
            onTrial={() => startTrial(it)}
            onPreview={() => {
              audio.play('ui.tap');
              setPreviewItem(it);
            }}
          />
        ))}
      </div>
      {previewItem && (
        <PreviewDialog
          title={`${previewItem.name} · ${t('chars.idle_preview')}`}
          onClose={() => setPreviewItem(null)}
        >
          <CharacterAnimationPreview character={previewItem} />
        </PreviewDialog>
      )}
      {toast && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            textAlign: 'center',
            background: toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function ShopCharacterCard({
  item, avatarColor, busy, canCoins, canGems, onBuy, onTrial, onPreview,
}: {
  item: CharacterItem;
  avatarColor: 'red' | 'blue';
  busy: boolean;
  canCoins: boolean;
  canGems: boolean;
  onBuy: (cur: 'coins' | 'gems') => void;
  onTrial: () => void;
  onPreview: () => void;
}) {
  const t = useT();
  const accent = RARITY_COLOR[item.rarity];
  const preview = resolveAssetUrl(item.idleUrl);
  return (
    <div
      className="card"
      style={{
        padding: 12, display: 'flex', gap: 12, alignItems: 'center',
        borderColor: item.owned ? `${accent}40` : `${accent}55`,
        opacity: item.owned ? 0.85 : 1,
      }}
    >
      <button
        type="button"
        onClick={onPreview}
        aria-label={`${t('common.preview')} ${item.name}`}
        style={{
          width: 60,
          height: 72,
          padding: 0,
          borderRadius: 10,
          background: 'rgba(0,0,0,0.3)',
          border: `1px solid ${accent}88`,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          cursor: 'zoom-in',
          boxShadow: `0 0 12px ${accent}22`,
        }}
      >
        {preview ? (
          <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
        ) : (
          <div style={{ transform: 'scale(1.1)', marginBottom: -4 }}>
            <Climber color={avatarColor} pose="idle" />
          </div>
        )}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="display" style={{ fontSize: 14, color: accent }}>{item.name}</span>
          <span style={{ fontSize: 8, color: accent, border: `1px solid ${accent}`, padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>
            {item.rarity}
          </span>
        </div>
        {item.description && (
          <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 3 }}>{item.description}</div>
        )}
        <div style={{ fontSize: 9, color: 'var(--ink-dim)', marginTop: 3, fontFamily: 'var(--f-mono)' }}>
          {t('chars.win_power', { power: item.stompPower.toFixed(1) })}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {item.owned ? (
            <span className="display" style={{ fontSize: 11, color: 'var(--c-cyan)', alignSelf: 'center' }}>
              {t('chars.owned_equip')}
            </span>
          ) : (
            <>
              {item.priceCoins > 0 && (
                <button
                  disabled={busy || !canCoins}
                  onClick={() => onBuy('coins')}
                  className="btn"
                  style={{
                    fontSize: 11, padding: '7px 12px',
                    background: 'rgba(244,199,36,0.15)', border: '1px solid var(--c-yellow)',
                    color: canCoins ? 'var(--c-yellow)' : 'var(--ink-dim)',
                  }}
                >
                  <CoinIcon size={13} /> {item.priceCoins}
                </button>
              )}
              {item.priceGems > 0 && (
                <button
                  disabled={busy || !canGems}
                  onClick={() => onBuy('gems')}
                  className="btn"
                  style={{
                    fontSize: 11, padding: '7px 12px',
                    background: 'rgba(155,93,229,0.15)', border: '1px solid var(--c-purple)',
                    color: canGems ? 'var(--c-purple)' : 'var(--ink-dim)',
                  }}
                >
                  💎 {item.priceGems}
                </button>
              )}
              {item.priceCoins === 0 && item.priceGems === 0 && (
                <span className="display" style={{ fontSize: 11, color: 'var(--c-cyan)', alignSelf: 'center' }}>
                  {t('chars.free_starter')}
                </span>
              )}
              {adsAvailable() && item.trialUsesLeft == null && (item.priceCoins > 0 || item.priceGems > 0) && (
                <button
                  disabled={busy}
                  onClick={onTrial}
                  className="btn"
                  style={{
                    fontSize: 11, padding: '7px 12px',
                    background: 'rgba(34,211,238,0.12)', border: '1px dashed var(--c-cyan)',
                    color: 'var(--c-cyan)',
                  }}
                >
                  📺 {t('shop.trial')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
